import { Cesium } from 'app/cesium';
import { EnforceLatestPromise } from 'app/shared/utilities/EnforceLatestPromise';
import _ from 'lodash';

interface Attribute<ValueType> {
    /**
     * True if this attribute requires that the entire primitive be recreated from scratch to be applied
     * or false if this attribute can be applied to an existing primitive
     */
    requiresPrimitiveRecreation: boolean;

    /**
     * A function that returns true if the two attribute values are considered equal or not
     */
    isEqual: (prev: ValueType, current: ValueType) => boolean;

    /**
     * A function that can copy this attribute value, to avoid mutations
     */
    copy(value: ValueType): ValueType;
}

type AttributeSpec<ValueTypes> = {
    [K in keyof ValueTypes]: Attribute<ValueTypes[K]>;
};

// eslint-disable-next-line @typescript-eslint/ban-types
export class AsyncPrimitive<T extends {}> {
    private readonly primitiveCollection: Cesium.PrimitiveCollection;
    private readonly singlePromiseQueue: EnforceLatestPromise;
    private readonly attributesSpec: AttributeSpec<T>;
    private readonly updatePrimitive: (prim: Cesium.Primitive, att: T) => void;
    private readonly createPrimitive: (att: T) => Cesium.Primitive;

    private primitive: Cesium.Primitive;
    private previousAttributes: T;
    private isDestroyed: boolean;

    /**
     *
     * Handles creating and updating an asynchronously drawn primitive.
     *  Some attributes can be specified as "update" meaning they can be applied to an existing primitive
     *  and other attributes can specify they recreate creating a new primitive from scratch
     *
     *  You only need to call {@link setAttributes} and the asynchronous update will be handled
     *   making sure only the most set of attributes is ever applied to the primitive
     *   and only 1 copy of the primitive is shown at once (swapped out once ready).
     *
     * @param primitiveCollection The collection to add and remove our primitive from
     * @param attributes The attributes we care about setting/updating/diffing on our primitive
     * @param updatePrimitive A function which can apply the "updatable" attributes to a given primitive
     * @param createPrimitive A function to create a new primitive from scratch, given a set of attributes
     */
    constructor(
        primitiveCollection: Cesium.PrimitiveCollection,
        attributes: AttributeSpec<T>,
        updatePrimitive: (prim: Cesium.Primitive, att: T) => void,
        createPrimitive: (att: T) => Cesium.Primitive,
    ) {
        this.isDestroyed = false;
        this.primitive = null;
        this.primitiveCollection = primitiveCollection;
        this.singlePromiseQueue = new EnforceLatestPromise();
        this.attributesSpec = attributes;
        this.updatePrimitive = updatePrimitive;
        this.createPrimitive = createPrimitive;
    }

    public async setAttributes(attributes: T): Promise<void> {
        if (this.isDestroyed) {
            throw new Error('async primitive is already destroyed!');
        }

        const diff = Object.entries(attributes).filter(([key, value]) => {
            const isEqual = this.attributesSpec[key].isEqual;
            const lastValue = this.previousAttributes ? this.previousAttributes[key] : null;
            return !isEqual(lastValue, value);
        });

        // no attribute was deemed different, meaning no update or copying is required
        if (!diff.length) {
            return;
        }

        // at least 1 attribute was different, take a copy of all attributes to keep as the "last used" set
        this.previousAttributes = cloneAttributes<T>(attributes, this.attributesSpec);

        const isRecreationRequired = diff.some(
            ([key]) => this.attributesSpec[key].requiresPrimitiveRecreation,
        );
        const isUpdateRequired = diff.some(
            ([key]) => !this.attributesSpec[key].requiresPrimitiveRecreation,
        );

        const hasPrimitive = !!this.primitive;
        if (isUpdateRequired && !isRecreationRequired && hasPrimitive) {
            this.updatePrimitive(this.primitive, attributes);
            this.orderPrimitiveCollection();
        } else if (isRecreationRequired || isUpdateRequired) {
            // create a fresh primitive, and we'll strategically swap it out with the old one
            const primitive = this.createPrimitive(attributes);
            // add the pending primitive to the collection
            this.primitiveCollection.add(primitive);

            try {
                await pollToPromise(() => {
                    return primitive.ready;
                });
            } catch {
                this.primitiveCollection.remove(primitive);
                return;
            }
            // wait for our primitive to be asynchronously created in the worker thread
            //  before we swap out any existing primitive

            // we're good to go!
            // remove the currently drawn primitive
            if (this.primitive) {
                this.primitiveCollection.remove(this.primitive);
            }
            // set the new primitive, it's already in the collection
            this.primitive = primitive;
            // make sure we apply the most recent attributes one more time
            //  because they may have changed while we waited for the primitive, but previousAttributes will always be correct
            this.updatePrimitive(this.primitive, this.previousAttributes);
            this.orderPrimitiveCollection();
        }
    }

    private orderPrimitiveCollection() {
        // get all the primitives
        const allPrimitives = [];
        for (let i = 0; i < this.primitiveCollection.length; i++) {
            const primitive: Cesium.Primitive = this.primitiveCollection.get(i);
            allPrimitives.push(primitive);
        }

        // group by zIndex
        const grouped = _.groupBy(allPrimitives, 'zIndex');

        Object.keys(grouped).forEach((group) => {
            // zIndex 0 gets ignored
            if (group == '0') {
                return;
            }
            // all other get zIndexes get put on top
            const primitiveArray: Cesium.Primitive[] = grouped[group];
            primitiveArray.forEach((primitive) => {
                this.primitiveCollection.raiseToTop(primitive);
            });
        });
    }

    public destroy(): void {
        this.isDestroyed = true;

        if (this.primitive) {
            // remove whatever is currently being rendered
            this.primitiveCollection.remove(this.primitive);
            this.primitive = null;
        }

        // make sure if there is a pending primitive it is not applied once it's ready!
        this.singlePromiseQueue.clearOldPromises();
    }
}

function cloneAttributes<T extends Record<string, unknown>>(
    attributes: T,
    spec: AttributeSpec<T>,
): T {
    // sorry, couldn't get TypeScript to play nice here
    return Object.fromEntries(
        Object.entries(attributes).map(([key, value]) => [key, spec[key].copy(value as any)]),
    ) as any;
}

// This is how Cesium's code suggests you wait for a primitive to  be ready (I know, wow). It appears that it takes a
// very small amount of time for a primitive to be reasy, so polling interval can be very small
function pollToPromise(f: () => boolean) {
    const pollInterval = 10;
    const timeout = 5000;

    return new Promise(function (resolve, reject) {
        const startTimestamp = Cesium.getTimestamp();
        const endTimestamp = startTimestamp + timeout;

        function poller() {
            let result = false;
            try {
                result = f();
            } catch (e) {
                reject(e);
                return;
            }

            if (result) {
                resolve(null);
            } else if (Cesium.getTimestamp() > endTimestamp) {
                reject(new Error(`Timeout - function did not complete within ${timeout}ms`));
            } else {
                setTimeout(poller, pollInterval);
            }
        }

        poller();
    });
}
